import React, { useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { useUserAlias } from 'src/hooks/useUserAlias';

const APPLICATION_ID = '8e7d4bf5-fcd1-4db3-95d1-0ac2f06d8207';
const APPLICATION_VERSION = '1.0.0';
const GUEST_ROLE_ARN = 'arn:aws:iam::627966481893:role/FinSpace-RUM-beta-CognitoDefaultUnauthenticatedRole-W6nAu2V2LaYs';
const IDENTITY_POOL_ID = 'us-west-2:091608ab-9741-46c0-998a-2e16e37c1063';
const RUM_ENDPOINT = 'https://dataplane.rum.us-west-2.amazonaws.com';
const APPLICATION_REGION = 'us-west-2';

let awsRum: AwsRum | null = null;

interface RumProviderProps {
  children: React.ReactNode;
}

export const RumProvider: React.FC<RumProviderProps> = ({ children }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const { alias } = useUserAlias();

  useEffect(() => {
    if (!awsRum && APPLICATION_ID && !APPLICATION_ID.includes('{{')) {
      initializeRum(!!user, alias);
    } else if (awsRum && user) {
      awsRum.addSessionAttributes({
        isAuthenticated: 'true',
        userAlias: alias,
      });
    }
  }, [user, alias]);

  return <>{children}</>;
};

function initializeRum(isAuthenticated: boolean, alias: string) {
  const config: AwsRumConfig = {
    sessionAttributes: {
      isAuthenticated: isAuthenticated ? 'true' : 'false',
      userAlias: alias || 'guest',
    },
    sessionSampleRate: 1,
    guestRoleArn: GUEST_ROLE_ARN,
    identityPoolId: IDENTITY_POOL_ID,
    endpoint: RUM_ENDPOINT,
    telemetries: ['performance', 'errors', 'http', 'interaction'],
    allowCookies: true,
    enableXRay: true,
    cookieAttributes: {
      domain: window.location.hostname,
      path: '/',
      sameSite: 'Lax',
      secure: true,
      unique: true,
    },
  };

  try {
    awsRum = new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, config);
    window.addEventListener('unhandledrejection', (event) => {
      awsRum?.recordError(event.reason);
    });
  } catch (error) {
    console.error('Failed to initialize CloudWatch RUM:', error);
  }
}

export const recordCustomEvent = (name: string, data?: Record<string, unknown>): void => {
  try {
    awsRum?.recordEvent(name, data || {});
  } catch (error) {
    console.error('Failed to record custom event:', error);
  }
};

export const recordError = (error: Error): void => {
  try {
    awsRum?.recordError(error);
  } catch (err) {
    console.error('Failed to record error:', err);
  }
};

export const addSessionAttributes = (
  attributes: Record<string, string | number | boolean>
): void => {
  try {
    awsRum?.addSessionAttributes(attributes);
  } catch (error) {
    console.error('Failed to add session attributes:', error);
  }
};
